import { gql } from '@apollo/client';
import { useQuery } from '@tbitop/api';
import { useGraphQLErrorHandler } from '@tbitop/error-handling';
import { usePagination } from '../../core/application/pagination';
import { KampagnenSortierung as Sortierkriterium, Sortierrichtung } from './__generated__/globalTypes';
import type { KampagnenSeitenweiseFragment, KampagnenSeitenweiseQuery, KampagnenSeitenweiseQueryVariables } from './__generated__/KampagnenSeitenweiseQuery';

export { Sortierkriterium, Sortierrichtung };
export type { KampagnenSeitenweiseFragment as Kampagne };

export const useKampagnenSeitenweiseQuery = (suchbegriff: string, sortierung: Sortierkriterium = Sortierkriterium.Name, sortierrichtung: Sortierrichtung = Sortierrichtung.Aufsteigend) => {
  const { handleWithErrorNotification } = useGraphQLErrorHandler();
  const { paginationVariables, fetchNextPage } = usePagination();

  const { data, loading, error, fetchMore, refetch } = useQuery<KampagnenSeitenweiseQuery, KampagnenSeitenweiseQueryVariables>(gql`
    query KampagnenSeitenweise($suchbegriff: String!, $anzahlProSeite: Int!, $seite: Int!, $sortierung: KampagnenSortierung!, $sortierrichtung: Sortierrichtung!) {
      kampagnenSeitenweise(suchbegriff: $suchbegriff, seite: $seite, anzahlProSeite: $anzahlProSeite, sortierung: $sortierung, sortierrichtung: $sortierrichtung) {
        beschreibung {
          gesamtAnzahl
          hatNachfolger
        }
        aktuelleSeite {
          ...KampagnenSeitenweise
        }
      }
    }
    fragment KampagnenSeitenweise on Kampagne {
      anlagedatum
      aufgabenId
      aufgabenTitel
      aufgabenTyp
      beschreibung
      faelligkeit
      name
    }
  `, {
    variables: {
      suchbegriff,
      sortierung,
      sortierrichtung,
      ...paginationVariables,
    },
    onError: handleWithErrorNotification('Es konnten keine Kampagnen geladen werden.'),
    notifyOnNetworkStatusChange: true,
  });

  return {
    data: data?.kampagnenSeitenweise?.aktuelleSeite ?? [],
    loading,
    refetch,
    error,
    total: data?.kampagnenSeitenweise?.beschreibung?.gesamtAnzahl,
    pagination: {
      fetchMore: () => fetchNextPage<KampagnenSeitenweiseQuery>(fetchMore)
        .catch(handleWithErrorNotification('Das Laden weiterer Kampagnen ist fehlgeschlagen.')),
      hasMore: data?.kampagnenSeitenweise?.beschreibung?.hatNachfolger ?? false,
    },
  };
};
